import { ElementRef, Injectable, inject } from '@angular/core';
import { SnackbarService } from '@warema/shared/helpers';
import { GoogleLocation, UserCoordinates, GoogleAction, SearchCoordinates } from '@warema/shared/models';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import PlaceResult = google.maps.places.PlaceResult;
import AutocompleteOptions = google.maps.places.AutocompleteOptions;
import Autocomplete = google.maps.places.Autocomplete;
import MapsEventListener = google.maps.MapsEventListener;

export interface CustomAutocompleteOptions extends AutocompleteOptions {
    region?: string;
    componentRestrictions?: { country: string };
}

@Injectable({
    providedIn: 'root',
})
export class GoogleService {
    private readonly _snackbar = inject(SnackbarService);

    private _autocomplete!: Autocomplete;
    private _autocompleteEvent!: MapsEventListener;
    private _searchElementRef!: ElementRef<HTMLInputElement>;
    private _disableAllSearch = false;
    private readonly _enterKeyCode = 13;
    private readonly _arrowDownKeyCode = 40;

    public initGoogleMapsAutoComplete(
        searchElementRef: ElementRef<HTMLInputElement>,
        autoCompleteOptions: CustomAutocompleteOptions,
    ): Observable<GoogleLocation> {
        this._searchElementRef = searchElementRef;
        const obs = new Subject<GoogleLocation>();

        this._autocomplete = new google.maps.places.Autocomplete(
            searchElementRef.nativeElement,
            autoCompleteOptions as AutocompleteOptions,
        );

        // request only these fields form Google to avoid additional unnecessary billing !
        this._autocomplete.setFields(['geometry.location', 'address_components']);

        this._registerEnterKeyLogic(searchElementRef);

        this._autocompleteEvent = this._autocomplete.addListener('place_changed', () => {
            const place: PlaceResult = this._autocomplete.getPlace();
            if (!place.geometry || !place.address_components) {
                this._disableAllSearch = false;
                return;
            }

            const formattedAddress = this._formatAddress(place);

            this._disableAllSearch = true;

            obs.next({
                event: GoogleAction.AUTO_COMPLETE,
                address: formattedAddress,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                latitude: place.geometry.location!.lat(),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                longitude: place.geometry.location!.lng(),
            });

            this._refreshUIInputState(searchElementRef);
            setTimeout(() => (this._disableAllSearch = false), 250);
        });

        return obs;
    }

    public getAddressByLatLong(
        searchElementRef: ElementRef<HTMLInputElement>,
        lat: number,
        long: number,
    ): Observable<GoogleLocation> {
        const obs = new Subject<GoogleLocation>();
        const geocoder = new google.maps.Geocoder();
        const latlng = new google.maps.LatLng(lat, long);

        if (this._disableAllSearch) {
            return obs;
        }

        void geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results != null) {
                const secondResult = results[1];
                if (secondResult) {
                    this._onGoogleResultFound(secondResult, obs, searchElementRef, lat, long);
                } else {
                    this._onGoogleResultNotFound(obs);
                }
            } else {
                this._onGoogleResultNotFound(obs);
            }
        });

        return obs;
    }

    public getUserCoordinatesForPreSelectedAddress(address: string): Observable<UserCoordinates> {
        const obs = new Subject<UserCoordinates>();

        const geocoder = new google.maps.Geocoder();
        void geocoder.geocode({ address }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results != null) {
                const place = results[0];

                obs.next(new UserCoordinates(place.geometry.location.lat(), place.geometry.location.lng()));
            }
        });

        return obs;
    }

    public getAddressCoordinates(address: string): Observable<SearchCoordinates> {
        return this.getUserCoordinatesForPreSelectedAddress(address).pipe(
            map((x: UserCoordinates): SearchCoordinates => ({ address, latitude: x.latitude, longitude: x.longitude })),
        );
    }

    public destroy(): void {
        google.maps.event.removeListener(this._autocompleteEvent);
        google.maps.event.clearInstanceListeners(this._autocomplete);
        this.removePacContainer();
        google.maps.event.clearListeners(this._searchElementRef.nativeElement, 'keydown');
    }

    public removePacContainer(): void {
        document.querySelector('.pac-container')?.remove();
    }

    private _getAddressComponent(
        components: Array<google.maps.GeocoderAddressComponent>,
        type: string,
    ): string | undefined {
        const component = components.find(comp => comp.types.includes(type));
        return component ? component.long_name : undefined;
    }

    private _formatAddress(place: google.maps.places.PlaceResult): string {
        if (!place?.address_components) {
            return '';
        }

        const postalCode = this._getAddressComponent(place.address_components, 'postal_code');
        const city = this._getAddressComponent(place.address_components, 'locality');
        const country = this._getAddressComponent(place.address_components, 'country');

        if (!city || !country) {
            return '';
        }

        let formattedAddress: string;
        if (postalCode) {
            if (postalCode.length >= 4) {
                formattedAddress = `${postalCode} ${city}, ${country}`;
            } else {
                formattedAddress = `${city}, ${country}`;
            }
        } else {
            formattedAddress = `${city}, ${country}`;
        }

        return formattedAddress;
    }

    private _registerEnterKeyLogic(searchElementRef: ElementRef<HTMLInputElement>): void {
        searchElementRef.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
            if (this._disableAllSearch || !searchElementRef.nativeElement.value) {
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();

                const isAnyAutocompleteResultGenerated = document.querySelector('.pac-item');
                if (!isAnyAutocompleteResultGenerated) {
                    void this._snackbar.showGenericErrorSnackbar('CAN_ONLY_SEARCH_WITHIN_YOUR_COUNTRY');
                    return;
                }

                // Check if an autocomplete suggestion is selected (highlighted)
                const highlightedResult = document.querySelector('.pac-item-selected');

                if (highlightedResult) {
                    // Scenario 1: A suggestion is already highlighted (arrow key navigation)
                    // Let Autocomplete handle the "Enter" event automatically
                    google.maps.event.trigger(searchElementRef.nativeElement, 'keydown', {
                        keyCode: this._enterKeyCode,
                    });
                } else {
                    // Scenario 2: No suggestion is highlighted (user didn't use arrow keys)
                    // Simulate "Arrow Down" key press to select the first suggestion
                    // after that the place_changed event will get triggered automatically
                    try {
                        google.maps.event.trigger(searchElementRef.nativeElement, 'keydown', {
                            keyCode: this._arrowDownKeyCode,
                        });
                    } catch (e: unknown) {
                        // catch this because the google maps lib throws this every time, but it works as expected
                        if (!(e instanceof TypeError && e.message?.includes('a.stopPropagation is not a function'))) {
                            throw e;
                        }
                    }
                }
            }
        });
    }

    private _refreshUIInputState(searchElementRef: ElementRef<HTMLInputElement>): void {
        searchElementRef.nativeElement.focus();
        searchElementRef.nativeElement.blur();
    }

    private _onGoogleResultFound(
        result: google.maps.GeocoderResult,
        obs: Subject<GoogleLocation>,
        searchElementRef: ElementRef<HTMLInputElement>,
        lat: number,
        long: number,
    ): void {
        if (result) {
            this._disableAllSearch = true;
            obs.next({
                event: GoogleAction.FIND_ME,
                address: result.formatted_address,
                latitude: lat,
                longitude: long,
            });

            this._refreshUIInputState(searchElementRef);

            setTimeout(() => (this._disableAllSearch = false), 250);
        } else {
            this._onGoogleResultNotFound(obs);
        }
    }

    private _onGoogleResultNotFound(obs: Subject<GoogleLocation>): void {
        obs.next({
            event: GoogleAction.NOT_FOUND,
            address: '',
            latitude: 0,
            longitude: 0,
        });
    }
}
